/**
 * PYRAVA® — component-slider.js v11
 *
 * v10 FIXED the width problem (explicit pixel widths via getBoundingClientRect).
 * Slide widths in the live HTML confirm this: 570px desktop, 430px tablet. ✅
 *
 * v10 BROKE navigation by forcing current = 0 and stripping Lumin's is-active.
 * Lumin re-sets is-active to slide 4 and scrolls there. Our current stays 0.
 * User sees slide 4, clicks Next → goTo(0+1=1) → jumps to slide 2. Wrong.
 *
 * v11 FIXES:
 *   A. Respect initial is-active from Lumin — set current from it on boot.
 *   B. MutationObserver syncs current when Lumin changes is-active on a slide.
 *   C. Debounced scroll listener syncs current from actual track.scrollLeft.
 *      This catches any scroll Lumin fires directly on the track element.
 *   D. Keep v10's explicit pixel widths (the core layout fix — don't revert).
 *   E. Keep v10's scroll-trigger class stripping + CSS opacity override.
 */

(function () {
  'use strict';

  if (!customElements.get('slider-component')) {
    customElements.define('slider-component', class extends HTMLElement {});
  }

  /* ════════════════════════════════════════
     STYLES
  ════════════════════════════════════════ */
  function injectStyles() {
    if (document.getElementById('pyrava-slider-styles')) return;
    const s = document.createElement('style');
    s.id = 'pyrava-slider-styles';
    s.textContent = `
      /* Neutralise scroll-trigger opacity on all product slides */
      ul.product__media-list li.product__media-item {
        opacity: 1 !important;
        transform: none !important;
        transition: none !important;
        visibility: visible !important;
      }

      /* Gap fill 750–989px */
      @media screen and (min-width: 750px) and (max-width: 989px) {
        ul.product__media-list.slider {
          flex-wrap: nowrap !important;
          overflow-x: scroll !important;
          overflow-y: hidden !important;
          scroll-snap-type: x mandatory !important;
          -webkit-overflow-scrolling: touch;
          scrollbar-width: none;
          -ms-overflow-style: none;
        }
        ul.product__media-list.slider::-webkit-scrollbar { display: none; }
        ul.product__media-list.slider .product__media-item {
          flex-shrink: 0 !important;
          scroll-snap-align: start !important;
        }
      }

      /* Desktop carousel 990px+ */
      @media screen and (min-width: 990px) {
        ul.product__media-list.slider {
          flex-direction: row !important;
          flex-wrap: nowrap !important;
          overflow-x: scroll !important;
          overflow-y: hidden !important;
          scroll-snap-type: x mandatory !important;
          -webkit-overflow-scrolling: touch;
          scrollbar-width: none;
          -ms-overflow-style: none;
        }
        ul.product__media-list.slider::-webkit-scrollbar { display: none; }
        ul.product__media-list.slider .product__media-item {
          flex-shrink: 0 !important;
          scroll-snap-align: start !important;
        }
      }

      /* Overlay arrows */
      .pyrava-arrow {
        position: absolute; top: 50%; transform: translateY(-50%);
        z-index: 99; width: 42px; height: 42px; border-radius: 50%;
        background: rgba(255,255,255,0.88);
        backdrop-filter: blur(10px); -webkit-backdrop-filter: blur(10px);
        border: 1.5px solid rgba(180,60,40,0.25);
        box-shadow: 0 2px 12px rgba(0,0,0,0.15);
        color: #1c1c1c; cursor: pointer;
        display: none; align-items: center; justify-content: center;
        transition: background .18s, border-color .18s, box-shadow .2s,
                    opacity .18s, transform .18s cubic-bezier(.22,1,.36,1);
        -webkit-tap-highlight-color: transparent;
        touch-action: manipulation; user-select: none;
      }
      .pyrava-arrow--prev { left: 12px; }
      .pyrava-arrow--next { right: 12px; }
      @media screen and (min-width: 990px) {
        .pyrava-arrow { width: 50px; height: 50px; }
        .pyrava-arrow--prev { left: 18px; }
        .pyrava-arrow--next { right: 18px; }
      }
      .pyrava-arrow svg { width: 18px; height: 18px; display: block; pointer-events: none; }
      @media screen and (min-width: 990px) { .pyrava-arrow svg { width: 20px; height: 20px; } }
      .pyrava-arrow:hover {
        background: rgba(255,255,255,0.98);
        border-color: rgba(180,60,40,0.55);
        box-shadow: 0 4px 20px rgba(0,0,0,0.18), 0 0 0 3px rgba(180,60,40,0.12);
        transform: translateY(-50%) scale(1.07);
      }
      .pyrava-arrow:active {
        transform: translateY(-50%) scale(0.92);
        background: rgba(180,60,40,0.10); border-color: rgba(180,60,40,0.6);
      }
      @keyframes pyrava-hint {
        0%,100% { box-shadow: 0 2px 12px rgba(0,0,0,0.15); }
        50%      { box-shadow: 0 2px 24px rgba(180,60,40,0.45); }
      }
      .pyrava-arrow--next { animation: pyrava-hint 1.8s ease-in-out 1s 2; }

      /* Dots — mobile/tablet only */
      .pyrava-dots {
        display: none; justify-content: center; align-items: center;
        gap: 6px; margin-top: 12px; padding: 0; list-style: none;
      }
      .pyrava-dot {
        width: 7px; height: 7px; border-radius: 50%;
        background: rgba(180,60,40,0.20); border: none; padding: 0;
        cursor: pointer; flex-shrink: 0;
        transition: width .28s cubic-bezier(.22,1,.36,1), background .22s;
        -webkit-tap-highlight-color: transparent;
      }
      .pyrava-dot.is-active { width: 24px; border-radius: 4px; background: #b43c28; }
      @media screen and (min-width: 990px) { .pyrava-dots { display: none !important; } }



      /* Thumbnail active outline */
      .thumbnail button.is-active,
      .thumbnail button[aria-current="true"] {
        outline: 2px solid #b43c28 !important; outline-offset: 2px;
      }
    `;
    document.head.appendChild(s);
  }

  /* ════════════════════════════════════════
     TRACK INLINE STYLES
  ════════════════════════════════════════ */
  const TRACK_STYLES = {
    display:                 'flex',
    flexDirection:           'row',
    flexWrap:                'nowrap',
    overflowX:               'scroll',
    overflowY:               'hidden',
    scrollSnapType:          'x mandatory',
    WebkitOverflowScrolling: 'touch',
    scrollbarWidth:          'none',
    msOverflowStyle:         'none',
  };

  /* Get viewer's actual rendered pixel width (forces layout flush) */
  function getViewerWidth(viewer) {
    const r = viewer.getBoundingClientRect();
    return r.width > 0 ? r.width : viewer.offsetWidth;
  }

  /* Apply carousel styles with explicit PIXEL widths on slides */
  function applyCarouselStyles(track, slides, viewer) {
    Object.assign(track.style, TRACK_STYLES);
    const w = getViewerWidth(viewer);
    slides.forEach(slide => {
      /* Strip scroll-trigger classes that cause opacity:0 */
      slide.classList.remove('scroll-trigger', 'animate--fade-in', 'animate--slide-in', 'scroll-trigger--offscreen');
      /* Explicit pixel widths — immune to "100% of 0px" problem */
      if (w > 0) {
        slide.style.flex     = `0 0 ${w}px`;
        slide.style.width    = `${w}px`;
        slide.style.minWidth = `${w}px`;
      } else {
        slide.style.flex     = '0 0 100%';
        slide.style.width    = '100%';
        slide.style.minWidth = '100%';
      }
      slide.style.scrollSnapAlign = 'start';
      slide.style.opacity         = '1';
      slide.style.transform       = 'none';
      slide.style.visibility      = 'visible';
    });
  }

  /* ════════════════════════════════════════
     MAIN GALLERY INIT
  ════════════════════════════════════════ */
  function initGallery() {
    const viewer = document.querySelector('[id^="GalleryViewer-"]');
    if (!viewer || viewer.__pyrava__) return;

    const track = viewer.querySelector('[id^="Slider-Gallery-"]');
    if (!track) return;

    const slides = Array.from(track.querySelectorAll('li[id^="Slide-"]'));
    if (!slides.length) return;

    viewer.__pyrava__ = true;

    /* Apply styles immediately */
    applyCarouselStyles(track, slides, viewer);

    /* Hide webkit scrollbar */
    const noBar = document.createElement('style');
    noBar.textContent = `#${CSS.escape(track.id)}::-webkit-scrollbar{display:none}`;
    document.head.appendChild(noBar);

    /* ────────────────────────────────────────────────────────
       FIX A: Read current from Lumin's is-active on boot.
       Lumin pre-selects a slide (typically slide 4). Honour it.
    ──────────────────────────────────────────────────────── */
    let current = 0;
    const initialActive = track.querySelector('li.is-active');
    if (initialActive) {
      const idx = slides.indexOf(initialActive);
      if (idx >= 0) current = idx;
    }

    /* ────────────────────────────────────────────────────────
       MutationObserver — re-apply styles AND sync current
       when Lumin/Dawn changes is-active on a slide.
    ──────────────────────────────────────────────────────── */
    let _locked = false;
    const mutationObserver = new MutationObserver((mutations) => {
      /* FIX B: sync current if Lumin changes is-active */
      for (const m of mutations) {
        if (m.attributeName === 'class' && slides.includes(m.target)) {
          if (m.target.classList.contains('is-active')) {
            const idx = slides.indexOf(m.target);
            if (idx >= 0 && idx !== current) {
              current = idx;
              updateUI();   // keep counter/dots in sync with Lumin
            }
          }
        }
      }
      if (_locked) return;
      _locked = true;
      applyCarouselStyles(track, slides, viewer);
      requestAnimationFrame(() => { _locked = false; });
    });

    mutationObserver.observe(track, { attributes: true, attributeFilter: ['style', 'class'] });
    mutationObserver.observe(viewer, { attributes: true, attributeFilter: ['style', 'class', 'data-desktop-layout'] });
    slides.forEach(slide => {
      mutationObserver.observe(slide, { attributes: true, attributeFilter: ['style', 'class'] });
    });

    /* Get current slide width for scroll calculations */
    function getSlideWidth() {
      if (slides[0]) {
        const r = slides[0].getBoundingClientRect();
        if (r.width > 0) return r.width;
      }
      return getViewerWidth(viewer);
    }

    /* Scroll track to current slide */
    function scrollTo(smooth) {
      const w = getSlideWidth();
      if (!w) return;
      track.scrollTo({ left: current * w, behavior: smooth ? 'smooth' : 'auto' });
    }

    /* ────────────────────────────────────────────────────────
       FIX C: Debounced scroll listener — sync current from
       actual track.scrollLeft after any scroll (incl. Lumin's).
       150ms debounce = fires after scrolling settles.
    ──────────────────────────────────────────────────────── */
    let _scrollSyncTimer = null;
    track.addEventListener('scroll', () => {
      clearTimeout(_scrollSyncTimer);
      _scrollSyncTimer = setTimeout(() => {
        const w = getSlideWidth();
        if (!w) return;
        const snapped = Math.round(track.scrollLeft / w);
        const clamped = Math.max(0, Math.min(slides.length - 1, snapped));
        if (clamped !== current) {
          current = clamped;
          slides.forEach(s => s.classList.remove('is-active'));
          if (slides[clamped]) slides[clamped].classList.add('is-active');
          updateUI();
          syncThumbnails();
        }
      }, 150);
    }, { passive: true });

    /* Navigate to a specific slide */
    function goTo(index) {
      if (index < 0 || index >= slides.length) return;
      slides.forEach(s => s.classList.remove('is-active'));
      slides[index].classList.add('is-active');
      current = index;
      scrollTo(true);
      updateUI();
      syncThumbnails();
      viewer.dispatchEvent(new CustomEvent('slideChanged', {
        detail: { currentPage: current + 1, currentElement: slides[current], totalPages: slides.length },
        bubbles: true,
      }));
    }

    /* Build overlay arrow */
    function makeArrow(dir) {
      const btn = document.createElement('button');
      btn.className = `pyrava-arrow pyrava-arrow--${dir}`;
      btn.setAttribute('aria-label', dir === 'prev' ? 'Previous image' : 'Next image');
      btn.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"
        fill="none" stroke="currentColor" stroke-width="2.5"
        stroke-linecap="round" stroke-linejoin="round">
        <polyline points="${dir === 'prev' ? '15 18 9 12 15 6' : '9 18 15 12 9 6'}"></polyline>
      </svg>`;
      btn.addEventListener('click', e => {
        e.preventDefault(); e.stopPropagation();
        goTo(current + (dir === 'prev' ? -1 : 1));
      });
      return btn;
    }

    const prevArrow = makeArrow('prev');
    const nextArrow = makeArrow('next');
    viewer.style.position = 'relative';
    viewer.style.display  = 'block';
    viewer.appendChild(prevArrow);
    viewer.appendChild(nextArrow);

    /* Touch / swipe */
    let tx = 0, ty = 0;
    track.addEventListener('touchstart', e => {
      tx = e.changedTouches[0].clientX;
      ty = e.changedTouches[0].clientY;
    }, { passive: true });
    track.addEventListener('touchend', e => {
      const dx = e.changedTouches[0].clientX - tx;
      const dy = Math.abs(e.changedTouches[0].clientY - ty);
      if (Math.abs(dx) < 40 || dy > Math.abs(dx)) return;
      goTo(current + (dx < 0 ? 1 : -1));
    }, { passive: true });

    /* Keyboard */
    viewer.setAttribute('tabindex', '0');
    viewer.addEventListener('keydown', e => {
      if (e.key === 'ArrowLeft')  goTo(current - 1);
      if (e.key === 'ArrowRight') goTo(current + 1);
    });

    /* Hide Dawn's built-in slider buttons */
    const dawnsButtons = viewer.querySelector('.slider-buttons');
    if (dawnsButtons) dawnsButtons.style.display = 'none';

    /* Dots */
    let dotsEl = null;
    if (slides.length > 1) {
      dotsEl = document.createElement('ol');
      dotsEl.className = 'pyrava-dots';
      slides.forEach((_, i) => {
        const li = document.createElement('li');
        const d  = document.createElement('button');
        d.className = 'pyrava-dot' + (i === current ? ' is-active' : '');
        d.setAttribute('aria-label', `Image ${i + 1}`);
        d.addEventListener('click', () => goTo(i));
        li.appendChild(d);
        dotsEl.appendChild(li);
      });
      track.insertAdjacentElement('afterend', dotsEl);
    }

    /* Counter removed — thumbnails already show position */

    /* Update arrows, dots, counter */
    function updateUI() {
      const mobile = window.innerWidth < 990;
      const total  = slides.length;

      prevArrow.style.display       = 'flex';
      nextArrow.style.display       = 'flex';
      prevArrow.style.opacity       = current === 0         ? '0.25' : '1';
      nextArrow.style.opacity       = current === total - 1 ? '0.25' : '1';
      prevArrow.style.pointerEvents = current === 0         ? 'none'  : 'auto';
      nextArrow.style.pointerEvents = current === total - 1 ? 'none'  : 'auto';

      if (dotsEl) {
        dotsEl.style.display = mobile ? 'flex' : 'none';
        dotsEl.querySelectorAll('.pyrava-dot').forEach((d, i) => {
          d.classList.toggle('is-active', i === current);
        });
      }


    }

    /* Sync thumbnail strip */
    function syncThumbnails() {
      const slide   = slides[current];
      const mediaId = slide ? slide.dataset.mediaId : null;
      if (!mediaId) return;

      const thumbList = document.querySelector('[id^="Slider-Thumbnails-"]');
      if (!thumbList) return;

      thumbList.querySelectorAll('.thumbnail-list__item').forEach(item => {
        const active = item.dataset.target === mediaId;
        const btn    = item.querySelector('button');
        if (btn) {
          btn.setAttribute('aria-current', active ? 'true' : 'false');
          btn.classList.toggle('is-active', active);
        }
      });

      const at = thumbList.querySelector('[aria-current="true"]');
      if (at) at.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    }

    /* ResizeObserver — re-calc pixel widths when grid column resolves */
    let _roTimer = null;
    if (typeof ResizeObserver !== 'undefined') {
      const ro = new ResizeObserver(() => {
        clearTimeout(_roTimer);
        _roTimer = setTimeout(() => {
          applyCarouselStyles(track, slides, viewer);
          scrollTo(false);
          updateUI();
        }, 16);
      });
      ro.observe(viewer);
    }

    /* Window resize */
    window.addEventListener('resize', () => { scrollTo(false); updateUI(); });

    /* Boot */
    scrollTo(false);
    updateUI();

    /* Safety retries — beat any late Dawn/Lumin scripts */
    [100, 400, 900, 1500].forEach(ms => {
      setTimeout(() => {
        applyCarouselStyles(track, slides, viewer);
        scrollTo(false);
      }, ms);
    });
  }

  /* ════════════════════════════════════════
     THUMBNAIL CLICK SYNC
  ════════════════════════════════════════ */
  function initThumbnailSync() {
    document.querySelectorAll('[id^="Slider-Thumbnails-"]').forEach(thumbList => {
      thumbList.querySelectorAll('.thumbnail-list__item').forEach((item, index) => {
        const btn = item.querySelector('button.thumbnail');
        if (!btn || btn.__pyrava_thumb__) return;
        btn.__pyrava_thumb__ = true;

        btn.addEventListener('click', () => {
          const viewer = document.querySelector('[id^="GalleryViewer-"]');
          if (!viewer) return;
          const track  = viewer.querySelector('[id^="Slider-Gallery-"]');
          if (!track) return;
          const slides = Array.from(track.querySelectorAll('li[id^="Slide-"]'));

          let w = slides[0] ? slides[0].getBoundingClientRect().width : 0;
          if (!w) w = viewer.getBoundingClientRect().width || viewer.offsetWidth || 0;

          slides.forEach(s => s.classList.remove('is-active'));
          if (slides[index]) slides[index].classList.add('is-active');
          if (w) track.scrollTo({ left: index * w, behavior: 'smooth' });

          thumbList.querySelectorAll('button.thumbnail').forEach(b => {
            b.setAttribute('aria-current', 'false');
            b.classList.remove('is-active');
          });
          btn.setAttribute('aria-current', 'true');
          btn.classList.add('is-active');



          const dots = viewer.querySelector('.pyrava-dots');
          if (dots) {
            dots.querySelectorAll('.pyrava-dot').forEach((d, i) => {
              d.classList.toggle('is-active', i === index);
            });
          }
        });
      });
    });
  }

  /* ════════════════════════════════════════
     BOOT
  ════════════════════════════════════════ */
  injectStyles();

  function boot() {
    initGallery();
    initThumbnailSync();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

})();
